import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatDate } from '../lib/api.js';
import AdminShell from './AdminShell.jsx';
import { IconPhotos, IconFilm } from '../lib/icons.jsx';

/**
 * The studio library: every wedding as a cover image rather than a table row,
 * for finding and revisiting past work.
 *
 * Opening one goes to the ordinary client gallery at /:slug. That's deliberate —
 * a signed-in admin now passes the password gate (see requireGalleryAccess), so
 * the photographer gets the exact experience their couples get, full-screen
 * lightbox and all, with nothing extra to build or keep in sync. The dashboard
 * remains the place for settings and uploads; this is purely for looking.
 */
export default function Library() {
  const [galleries, setGalleries] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/api/admin/galleries').then(setGalleries).catch((e) => setError(e.message));
  }, []);

  const shown = useMemo(() => {
    if (!galleries) return [];
    const q = query.trim().toLowerCase();
    if (!q) return galleries;
    return galleries.filter((g) => g.client_name.toLowerCase().includes(q) || (g.wedding_date || '').includes(q));
  }, [galleries, query]);

  return (
    <AdminShell
      actions={
        <Link className="btn sm" to="/admin">
          Dashboard
        </Link>
      }
    >
      <div className="spread" style={{ marginBottom: '1.5rem', alignItems: 'flex-end' }}>
        <div>
          <span className="label">The Studio</span>
          <h1 className="serif" style={{ margin: '0.35rem 0 0' }}>Library</h1>
        </div>
        {galleries?.length > 0 && (
          <input
            className="lib-search"
            placeholder="Search by name or year…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {!galleries && !error && <p className="muted">Loading…</p>}

      {galleries && shown.length === 0 && (
        <div className="card muted">
          {query ? `Nothing matches “${query}”.` : 'No galleries yet. Create one from the dashboard to get started.'}
        </div>
      )}

      <div className="lib-grid">
        {shown.map((g) => (
          <a key={g.id} className="lib-card" href={`/${g.slug}`} target="_blank" rel="noreferrer">
            <span className="lib-thumb">
              {g.cover_url ? (
                <img src={g.cover_url} alt="" loading="lazy" />
              ) : (
                <span className="lib-empty label">No photos yet</span>
              )}
              <span className="lib-veil" />
              <span className="lib-body">
                <span className="lib-name serif">{g.client_name}</span>
                <span className="lib-meta">
                  {g.wedding_date ? formatDate(g.wedding_date) : 'No date'}
                  {g.stats.photos > 0 && (
                    <>
                      {' · '}
                      <IconPhotos width={12} height={12} /> {g.stats.photos}
                    </>
                  )}
                  {g.stats.videos > 0 && (
                    <>
                      {' · '}
                      <IconFilm width={12} height={12} /> {g.stats.videos}
                    </>
                  )}
                </span>
              </span>
            </span>
          </a>
        ))}
      </div>
    </AdminShell>
  );
}
